/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, ModalBody } from 'reactstrap';
import { useSelector } from 'react-redux';

import AuthHeader from 'shared/components/layout/AuthHeader';
import Footer from 'shared/components/layout/Footer';
import { ReactComponent as UserIcon } from 'assets/icon-user.svg';
import { ReactComponent as LockIcon } from 'assets/icon-lock.svg';
import { TextField, PasswordField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import { colors, device } from 'shared/defaultTheme';
import { getHeaderFooterColorData } from 'shared/utils';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { Information, InformationModal } from 'shared/components/SharedStyles/SharedStyles';
import InformationPersonal from 'shared/components/InformationPersonal';

const CardExternalSiteWrapper = styled(Card)`
  height: auto;
  box-shadow: 0 2px 6px 0 #aaaeb0;
  padding-bottom: 0;

  @media ${device.sm} {
    width: 100%;
  }
  @media ${device.md} {
    width: 100%;
  }
  @media ${device.lg} {
    width: 85%;
  }
  @media ${device.xl} {
    width: 85%;
  }

  .card-body {
    padding: 0;

    .auth-layout {
      height: auto;

      .account__wrapper {
        padding-top: 20px;
        padding-bottom: 20px;
        .account__card {
          box-shadow: 0 2px 6px 0 #aaaeb0;
          .form__form-group-field {
            border: 1px solid ${colors.ghostWhite};
          }
        }
      }

      .footer {
        @media ${device.sm} {
          padding: 32px 0 31.5px 0;
        }
        @media ${device.md} {
          padding: 32px 0 31.5px 0;
        }
        @media ${device.lg} {
          padding: 32px 50px 31.5px 50px;
        }
        @media ${device.xl} {
          padding: 32px 50px 31.5px 50px;
        }
      }
    }
  }
`;

const Logo = styled.div(({ imageSrc }) => ({
  background: `url('${imageSrc}')`,
  backgroundPosition: 'center',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  width: '100%',
  height: '49px',
}));

const LineColor = styled.div(({ height, borderWidth, color, marginTop }) => ({
  height,
  boxSizing: 'border-box',
  borderWidth,
  borderColor: color,
  borderStyle: 'solid',
  marginTop,
  width: '100%',
}));

const PreviewExternalSite = ({
  logo,
  mainColor,
  secondaryColor,
  headerOption,
  footerOption,
  whiteLogo,
  footerText,
  phone,
  showLogoFooter,
}) => {
  const { t } = useTranslation();
  const headerDataColor = getHeaderFooterColorData(headerOption, mainColor, logo, whiteLogo);
  const footerDataColor = getHeaderFooterColorData(footerOption, mainColor, logo, whiteLogo);
  const theme = useSelector((state) => state.theme);

  const [openInformationModal, setOpenInformationModal] = useState(false);

  const onCloseInformationModal = () => {
    setOpenInformationModal(false);
  };

  return (
    <div className="general-customizer__preview-wrapper">
      <CardExternalSiteWrapper>
        <CardBody>
          <div className="auth-layout">
            <AuthHeader logoSrc={logo} lineColor={mainColor} backgroundColor={mainColor} />
            <div className="account">
              <div className="account__wrapper">
                <div className="account__card">
                  <Logo imageSrc={headerDataColor.logo} style={{ backgroundColor: mainColor }} />
                  <LineColor color={secondaryColor} height={3} borderWidth={2} marginTop={18.44} />
                  <form className="form mt-5">
                    <div className="form__form-group form__form-group--user">
                      <TextField
                        placeholder={t('login_username')}
                        Icon={() => <UserIcon fill={secondaryColor} />}
                        borderRadius={2}
                        value=""
                        onChange={() => null}
                      />
                    </div>
                    <div className="form__form-group form__form-group--password">
                      <PasswordField
                        placeholder={t('login_password')}
                        Icon={() => <LockIcon fill={secondaryColor} />}
                        borderRadius={2}
                        value=""
                        onChange={() => null}
                      />
                    </div>
                    <div className="account__remember-me__wrapper">
                      <span className="form__label">{t('login_forgot_password')}</span>
                    </div>
                    <Button
                      className="account_login-btn"
                      type="button"
                      label={t('login_btn_session')}
                      color="primary"
                      full
                    />
                    <LineColor color={secondaryColor} height={1} borderWidth={1} marginTop={0} />
                    <Information className="d-flex justify-content-center w-100 mt-3">
                      <a href onClick={() => setOpenInformationModal(true)}>
                        {t('login_information_data_personals')}
                      </a>
                    </Information>
                    <InformationModal
                      isOpen={openInformationModal}
                      toggle={onCloseInformationModal}
                      size="lg"
                    >
                      <ModalHeader
                        className="modal-header"
                        backgroundColor={colors.transparent}
                        onClose={onCloseInformationModal}
                      />
                      <ModalBody>
                        <InformationPersonal onCancel={onCloseInformationModal} />
                      </ModalBody>
                    </InformationModal>
                  </form>
                </div>
              </div>
            </div>
            <Footer
              backgroundColor={footerDataColor.backgroundColor}
              logoSrc={footerDataColor.logo}
              footerText={footerText}
              iconColor={footerDataColor.iconColor}
              textColor={theme.footerTextColor}
              facebookLink={theme.facebookLink}
              linkedinLink={theme.linkedinLink}
              youtubeLink={theme.youtubeLink}
              twitterLink={theme.twitterLink}
              phone={phone}
              showLogoFooter={showLogoFooter}
            />
          </div>
        </CardBody>
      </CardExternalSiteWrapper>
    </div>
  );
};

PreviewExternalSite.defaultProps = {
  logo: '',
  mainColor: '',
  secondaryColor: '',
  headerOption: 1,
  footerOption: 1,
  whiteLogo: '',
  footerText: '',
  phone: '',
  showLogoFooter: false,
};

PreviewExternalSite.propTypes = {
  logo: PropTypes.string,
  mainColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  headerOption: PropTypes.number,
  footerOption: PropTypes.number,
  whiteLogo: PropTypes.string,
  footerText: PropTypes.string,
  phone: PropTypes.string,
  showLogoFooter: PropTypes.bool,
};

export default PreviewExternalSite;
